import { useMutation, useQuery } from '@tanstack/react-query'
import { CreateDailyNoteDto, UpdateDailyNoteDto, ViewDailyNote } from '@xeetr/types/models'
import { AxiosError } from 'axios'
import { useContext } from 'react'

import { ActiveTenantContext } from '../context/common/Context'
import { invalidateDailyNoteQueries } from '../lib/mutationHelpers'
import { queryKeys } from '../lib/queryKeys'
import { DailyNoteService } from '../services/DailyNote.service'

import { useTenantOptionalService } from './common/useServiceFactory'

import {
  isInsufficientPermissionsError,
  showErrorToastUnlessPermissionDenied,
  showToast,
} from '@/utils/toastMessage'

interface UseDailyNotesReturn {
  notes: ViewDailyNote[]
  isLoading: boolean
  isError: boolean
  error: Error | null

  create: (note: CreateDailyNoteDto, callback?: () => void) => void
  update: (id: string, note: UpdateDailyNoteDto, callback?: () => void) => void
  remove: (id: string, callback?: () => void) => void

  isCreating: boolean
  isUpdating: boolean
  isDeleting: boolean

  refetch: () => void
}

/**
 * Modern hook for managing daily notes with React Query.
 *
 * Features:
 * - Fetches daily notes for a date range
 * - Create, update, and delete notes with optimistic updates
 * - Automatic cache invalidation
 * - Toast notifications for success/error states
 *
 * @param from - Start date in YYYY-MM-DD format
 * @param until - End date in YYYY-MM-DD format
 *
 * @example
 * ```tsx
 * const { notes, isLoading, create, update, remove } = useDailyNotes('2025-01-01', '2025-01-31')
 * ```
 */
export const useDailyNotes = (from?: string, until?: string): UseDailyNotesReturn => {
  const { tenantId } = useContext(ActiveTenantContext)

  const getService = useTenantOptionalService(DailyNoteService)

  const {
    data: notes = [],
    isLoading,
    isError,
    error,
    refetch,
  } = useQuery({
    queryKey: queryKeys.dailyNotes.list(tenantId, from, until),
    queryFn: async () => {
      const service = await getService()
      return service.getDailyNotes(from, until)
    },
    enabled: !!tenantId,
  })

  const createMutation = useMutation({
    mutationFn: async (note: CreateDailyNoteDto) => {
      const service = await getService()
      return service.createDailyNote(note)
    },
    onSuccess: () => {
      if (!tenantId) return
      invalidateDailyNoteQueries(tenantId)
      showToast({ severity: 'success', detail: 'Die Notiz wurde erfolgreich erstellt!' })
    },
    onError: (error: AxiosError) => {
      if (isInsufficientPermissionsError(error)) return
      if (error.response?.status === 409) {
        showToast({
          severity: 'error',
          detail: 'Für diesen Tag existiert bereits eine Notiz!',
        })
      } else {
        showToast({ severity: 'error' })
      }
    },
  })

  const updateMutation = useMutation({
    mutationFn: async ({ id, note }: { id: string; note: UpdateDailyNoteDto }) => {
      const service = await getService()
      return service.updateDailyNote(id, note)
    },
    onSuccess: () => {
      if (!tenantId) return
      invalidateDailyNoteQueries(tenantId)
      showToast({ severity: 'success', detail: 'Die Notiz wurde erfolgreich aktualisiert!' })
    },
    onError: (error) => {
      showErrorToastUnlessPermissionDenied(error, { severity: 'error' })
    },
  })

  const deleteMutation = useMutation({
    mutationFn: async (id: string) => {
      const service = await getService()
      return service.deleteDailyNote(id)
    },
    onSuccess: () => {
      if (!tenantId) return
      invalidateDailyNoteQueries(tenantId)
      showToast({ severity: 'success', detail: 'Die Notiz wurde erfolgreich gelöscht!' })
    },
    onError: (error) => {
      showErrorToastUnlessPermissionDenied(error, { severity: 'error' })
    },
  })

  return {
    notes,
    isLoading,
    isError,
    error: error as Error | null,

    create: (note, callback) => {
      createMutation.mutate(note, { onSuccess: callback })
    },
    update: (id, note, callback) => {
      updateMutation.mutate({ id, note }, { onSuccess: callback })
    },
    remove: (id, callback) => {
      deleteMutation.mutate(id, { onSuccess: callback })
    },

    isCreating: createMutation.isPending,
    isUpdating: updateMutation.isPending,
    isDeleting: deleteMutation.isPending,

    refetch,
  }
}
